import { useState, useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import {
  ChevronDown,
  MoreVertical,
  RotateCw,
  RefreshCw,
  Play,
  Square,
  Globe,
  Pencil,
  CopyPlus,
  Share2,
  FolderOpen,
  FolderPlus,
  Trash2,
  Loader2,
  Check,
  Save,
  X,
} from 'lucide-react'
import {
  startApp,
  stopApp,
  deleteApp,
  type App,
  type Folder,
} from '../../lib/api'
import { getCloneNotice } from '../../lib/cloneFeedback'
import { usePublishApp } from '../../hooks/usePublishApp'
import { useFolders } from '../folders/useFolders'
import { FOLDER_COLORS } from '../folders/folderColors'
import EditAppInfoModal from '../EditAppInfoModal'
import ShareModalComponent from '../ShareModal'
import CloneAppModal from '../CloneAppModal'
import AppDeleteDialog from '../AppDeleteDialog'

interface WorkspaceContextMenuProps {
  app: App | null
  // Workspace ops
  onSaveWork: () => void
  onRestartWorkspace: () => Promise<void>
  onRecreateWorkspace: () => Promise<void>
  onRebuildApp: () => Promise<void>
  saving: boolean
  restarting: boolean
  recreating: boolean
  rebuilding: boolean
  // Notify parent when app metadata changes (Edit Info, etc.)
  onAppUpdated?: (app: App) => void
}

const itemClass =
  'w-full px-3 py-2 text-left text-sm hover:bg-secondary flex items-center gap-2 outline-none cursor-pointer disabled:opacity-50'

function FolderCreateRow({ onCreate }: { onCreate: (name: string) => void }) {
  const [editing, setEditing] = useState(false)
  const [value, setValue] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (editing && inputRef.current) inputRef.current.focus()
  }, [editing])

  const commit = () => {
    const trimmed = value.trim()
    if (trimmed) onCreate(trimmed)
    setValue('')
    setEditing(false)
  }

  if (editing) {
    return (
      <div className="px-2 py-2 flex items-center gap-2">
        <FolderPlus className="h-4 w-4 text-muted-foreground shrink-0" />
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              commit()
            } else if (e.key === 'Escape') {
              e.preventDefault()
              setValue('')
              setEditing(false)
            }
          }}
          onBlur={() => {
            if (value.trim()) commit()
            else {
              setValue('')
              setEditing(false)
            }
          }}
          placeholder="Folder name"
          className="flex-1 text-sm bg-transparent outline-none min-w-0"
        />
      </div>
    )
  }

  return (
    <button
      type="button"
      onClick={() => setEditing(true)}
      className="w-full px-2 py-2 rounded hover:bg-secondary cursor-pointer flex items-center gap-2 text-sm text-left"
    >
      <FolderPlus className="h-4 w-4" />
      New folder...
    </button>
  )
}

export default function WorkspaceContextMenu({
  app,
  onSaveWork,
  onRestartWorkspace,
  onRecreateWorkspace,
  onRebuildApp,
  saving,
  restarting,
  recreating,
  rebuilding,
  onAppUpdated,
}: WorkspaceContextMenuProps) {
  const navigate = useNavigate()

  const myPermission = app?.my_permission ?? 'owner'
  const canEdit = ['owner', 'admin', 'edit'].includes(myPermission)
  const canDelete = myPermission === 'owner'

  // Modal / confirm visibility
  const [showRestartConfirm, setShowRestartConfirm] = useState(false)
  const [showRecreateConfirm, setShowRecreateConfirm] = useState(false)
  const [showRebuildConfirm, setShowRebuildConfirm] = useState(false)
  const [showEditInfo, setShowEditInfo] = useState(false)
  const [showShareModal, setShowShareModal] = useState(false)
  const [showCloneModal, setShowCloneModal] = useState(false)
  const [showFolderModal, setShowFolderModal] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)

  const [actionLoading, setActionLoading] = useState<string | null>(null)
  const [shareCopied, setShareCopied] = useState(false)

  // Folders (Move to Folder submenu) — uses same hook AppDetail does.
  const {
    folders,
    addAppToFolder,
    removeAppFromFolder,
    createFolderAndAddApp,
    getAppFolderIds,
  }: {
    folders: Folder[]
    addAppToFolder: (folderId: number, appName: string) => Promise<void>
    removeAppFromFolder: (folderId: number, appName: string) => Promise<void>
    createFolderAndAddApp: (name: string, appName: string) => Promise<Folder>
    getAppFolderIds: (appName: string) => number[]
  } = useFolders(app ? [app] : [], 'my-apps')
  const hasFolders = folders.length > 0
  const appFolderIds = app ? getAppFolderIds(app.name) : []

  const { startPublish, modal: publishModal, isPublishing } = usePublishApp()

  async function runAction(name: string, fn: () => Promise<void>) {
    setActionLoading(name)
    try {
      await fn()
    } finally {
      setActionLoading(null)
    }
  }

  async function handleStart() {
    if (!app) return
    await runAction('start', async () => {
      await startApp(app.name)
      // Reload the page so the workspace observes the new running state
      // (deployment phase, preview URL, etc.).
      window.location.reload()
    })
  }

  async function handleStop() {
    if (!app) return
    await runAction('stop', async () => {
      await stopApp(app.name)
      window.location.reload()
    })
  }

  async function handleDelete() {
    if (!app) return
    await runAction('delete', async () => {
      await deleteApp(app.name)
      navigate('/')
    })
  }

  return (
    <>
      <DropdownMenu.Root>
        <DropdownMenu.Trigger asChild>
          <button
            type="button"
            title="App and workspace actions"
            aria-label="Actions"
            className="flex items-center gap-1.5 text-sm text-foreground/70 hover:text-foreground transition-colors px-1.5 py-1 rounded hover:bg-secondary"
          >
            <MoreVertical className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Actions</span>
            <ChevronDown className="w-3 h-3" />
          </button>
        </DropdownMenu.Trigger>

        <DropdownMenu.Portal>
          <DropdownMenu.Content
            className="w-56 bg-card border border-border rounded-lg shadow-lg z-50 py-1"
            align="end"
            sideOffset={4}
          >
            {app && (
              <>
                {/* Workspace section */}
                <DropdownMenu.Label className="px-3 py-1 text-[11px] uppercase tracking-wide text-muted-foreground">
                  Workspace
                </DropdownMenu.Label>
                <DropdownMenu.Item
                  className={itemClass}
                  disabled={saving}
                  onSelect={() => onSaveWork()}
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  Save work
                </DropdownMenu.Item>
                <DropdownMenu.Item
                  className={itemClass}
                  disabled={restarting}
                  onSelect={() => setShowRestartConfirm(true)}
                >
                  <RotateCw className={`w-4 h-4 ${restarting ? 'animate-spin' : ''}`} />
                  Restart workspace
                </DropdownMenu.Item>
                <DropdownMenu.Item
                  className={itemClass}
                  disabled={recreating}
                  onSelect={() => setShowRecreateConfirm(true)}
                >
                  <RefreshCw className={`w-4 h-4 ${recreating ? 'animate-spin' : ''}`} />
                  Recreate workspace
                </DropdownMenu.Item>

                <DropdownMenu.Separator className="h-px bg-border my-1" />

                {/* App section */}
                <DropdownMenu.Label className="px-3 py-1 text-[11px] uppercase tracking-wide text-muted-foreground">
                  App
                </DropdownMenu.Label>

                {canEdit && (
                  app.status === 'stopped' || app.status === 'error' ? (
                    <DropdownMenu.Item
                      className={`${itemClass} text-green-500`}
                      disabled={actionLoading !== null}
                      onSelect={() => handleStart()}
                    >
                      {actionLoading === 'start' ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Play className="w-4 h-4" />
                      )}
                      Start app
                    </DropdownMenu.Item>
                  ) : (
                    <DropdownMenu.Item
                      className={itemClass}
                      disabled={actionLoading !== null}
                      onSelect={() => handleStop()}
                    >
                      {actionLoading === 'stop' ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Square className="w-4 h-4" />
                      )}
                      Stop app
                    </DropdownMenu.Item>
                  )
                )}

                {canEdit && (
                  <DropdownMenu.Item
                    className={`${itemClass} text-orange-500`}
                    disabled={rebuilding}
                    onSelect={() => setShowRebuildConfirm(true)}
                  >
                    {rebuilding ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <RefreshCw className="w-4 h-4" />
                    )}
                    Rebuild
                  </DropdownMenu.Item>
                )}

                {canEdit && (
                  <DropdownMenu.Item
                    className={`${itemClass} text-purple-500`}
                    disabled={isPublishing || actionLoading !== null}
                    onSelect={(e) => {
                      if (isPublishing) {
                        e.preventDefault()
                        return
                      }
                      void startPublish(app)
                    }}
                  >
                    {isPublishing ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Globe className="w-4 h-4" />
                    )}
                    {isPublishing ? 'Publishing…' : 'Publish'}
                  </DropdownMenu.Item>
                )}

                <DropdownMenu.Separator className="h-px bg-border my-1" />

                {canEdit && (
                  <DropdownMenu.Item
                    className={itemClass}
                    onSelect={() => setShowEditInfo(true)}
                  >
                    <Pencil className="w-4 h-4" />
                    Edit info
                  </DropdownMenu.Item>
                )}

                {canEdit && (
                  <DropdownMenu.Item
                    className={itemClass}
                    onSelect={() => setShowCloneModal(true)}
                  >
                    <CopyPlus className="w-4 h-4" />
                    Clone
                  </DropdownMenu.Item>
                )}

                <DropdownMenu.Item
                  className={itemClass}
                  onSelect={(e) => {
                    if (canEdit) {
                      setShowShareModal(true)
                    } else {
                      // View-only fallback: copy URL and keep menu open to show
                      // the "URL copied!" feedback before resetting the label.
                      e.preventDefault()
                      navigator.clipboard.writeText(app.url)
                      setShareCopied(true)
                      setTimeout(() => setShareCopied(false), 1500)
                    }
                  }}
                >
                  <Share2 className="w-4 h-4" />
                  {shareCopied ? 'URL copied!' : 'Share'}
                </DropdownMenu.Item>

                {canEdit && (
                  <DropdownMenu.Item
                    className={itemClass}
                    onSelect={() => setShowFolderModal(true)}
                  >
                    <FolderOpen className="w-4 h-4" />
                    Move to folder
                  </DropdownMenu.Item>
                )}
              </>
            )}

            {/* Delete (owner only) */}
            {app && canDelete && (
              <>
                <DropdownMenu.Separator className="h-px bg-border my-1" />
                <DropdownMenu.Item
                  className={`${itemClass} text-destructive`}
                  onSelect={() => setDeleteDialogOpen(true)}
                >
                  <Trash2 className="w-4 h-4" />
                  Delete
                </DropdownMenu.Item>
              </>
            )}
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>

      {/* Restart confirm */}
      {showRestartConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card border border-border rounded-xl w-full max-w-md p-6">
            <h2 className="text-lg font-semibold mb-2 flex items-center gap-2">
              <RotateCw className="w-5 h-5" />
              Restart workspace container
            </h2>
            <p className="text-muted-foreground text-sm mb-4">
              This will restart the workspace container and interrupt any running processes.
              The page will reload automatically.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowRestartConfirm(false)}
                className="px-4 py-2 rounded-lg bg-secondary hover:bg-secondary/80 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setShowRestartConfirm(false)
                  void onRestartWorkspace()
                }}
                className="px-4 py-2 rounded-lg bg-destructive text-destructive-foreground hover:bg-destructive/90 transition-colors"
              >
                Restart
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Recreate confirm */}
      {showRecreateConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card border border-border rounded-xl w-full max-w-md p-6">
            <h2 className="text-lg font-semibold mb-2 flex items-center gap-2">
              <RefreshCw className="w-5 h-5" />
              Recreate workspace container
            </h2>
            <p className="text-muted-foreground text-sm mb-4">
              Replaces the workspace container with a fresh one — pulls the latest image and
              re-injects platform environment variables. Workspace files are preserved (only the
              container is replaced); running processes are stopped. Use this after a platform
              upgrade if Restart isn't picking up new behavior.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowRecreateConfirm(false)}
                className="px-4 py-2 rounded-lg bg-secondary hover:bg-secondary/80 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setShowRecreateConfirm(false)
                  void onRecreateWorkspace()
                }}
                className="px-4 py-2 rounded-lg bg-destructive text-destructive-foreground hover:bg-destructive/90 transition-colors"
              >
                Recreate
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Rebuild confirm */}
      {showRebuildConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card border border-border rounded-xl w-full max-w-md p-6">
            <h2 className="text-lg font-semibold mb-2 flex items-center gap-2">
              <RefreshCw className="w-5 h-5 text-orange-500" />
              Rebuild app
            </h2>
            <p className="text-muted-foreground text-sm mb-4">
              This will rebuild your app from source and reinstall all dependencies.
              The app will be briefly unavailable during the build.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowRebuildConfirm(false)}
                className="px-4 py-2 rounded-lg bg-secondary hover:bg-secondary/80 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setShowRebuildConfirm(false)
                  void onRebuildApp()
                }}
                className="px-4 py-2 rounded-lg bg-orange-500 text-white hover:bg-orange-600 transition-colors"
              >
                Rebuild
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Info modal */}
      {app && showEditInfo && (
        <EditAppInfoModal
          app={app}
          onClose={() => setShowEditInfo(false)}
          onSaved={(updated) => {
            setShowEditInfo(false)
            onAppUpdated?.(updated)
            // If the slug changed, navigate the workspace to the new app name.
            if (updated.name !== app.name) {
              navigate(`/workspace?app=${encodeURIComponent(updated.name)}`)
            }
          }}
        />
      )}

      {/* Share modal */}
      {app && showShareModal && (
        <ShareModalComponent
          app={app}
          onClose={() => setShowShareModal(false)}
        />
      )}

      {/* Clone modal */}
      {app && showCloneModal && (
        <CloneAppModal
          app={app}
          onClose={() => setShowCloneModal(false)}
          onCloned={(response) => {
            setShowCloneModal(false)
            navigate(`/apps/${response.name}`, {
              state: { cloneNotice: getCloneNotice(response) },
            })
          }}
        />
      )}

      {/* Delete dialog */}
      {app && canDelete && (
        <AppDeleteDialog
          app={app}
          open={deleteDialogOpen}
          onOpenChange={setDeleteDialogOpen}
          onDelete={handleDelete}
        />
      )}

      {/* Move to folder modal — replaces a Radix submenu so it stays on screen
          on mobile (the submenu was getting cut off past the right edge). */}
      {app && canEdit && showFolderModal && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={() => setShowFolderModal(false)}
        >
          <div
            className="bg-card border border-border rounded-xl w-full max-w-sm p-5"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <FolderOpen className="w-5 h-5" />
                Move to folder
              </h2>
              <button
                type="button"
                onClick={() => setShowFolderModal(false)}
                className="rounded p-1 text-muted-foreground hover:bg-secondary hover:text-foreground transition-colors"
                aria-label="Close"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="max-h-[60vh] overflow-y-auto -mx-1 px-1">
              <FolderCreateRow
                onCreate={(name) => { void createFolderAndAddApp(name, app.name) }}
              />
              {hasFolders && <div className="my-2 h-px bg-border" />}
              {folders.map((folder) => {
                const colorSet = FOLDER_COLORS[folder.color] || FOLDER_COLORS.gray
                const isInFolder = appFolderIds.includes(folder.id)
                return (
                  <button
                    key={folder.id}
                    type="button"
                    onClick={() => {
                      if (isInFolder) {
                        void removeAppFromFolder(folder.id, app.name)
                      } else {
                        void addAppToFolder(folder.id, app.name)
                      }
                    }}
                    className="w-full px-2 py-2 rounded hover:bg-secondary cursor-pointer flex items-center gap-2 text-sm text-left"
                  >
                    <span className={`w-2.5 h-2.5 rounded-full ${colorSet.dot} shrink-0`} />
                    <span className="flex-1 truncate">{folder.name}</span>
                    {isInFolder && <Check className="w-4 h-4 text-primary shrink-0" />}
                  </button>
                )
              })}
              {!hasFolders && (
                <p className="text-xs text-muted-foreground px-2 py-3">
                  No folders yet — create one above.
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Publish modal */}
      {publishModal}
    </>
  )
}
